import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Typography, Grid, Button, Stack, Chip, Card, CardContent,
  CircularProgress, Divider, Slider, TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ShoppingCart as CartIcon,
  TrendingUp as ProfitIcon,
} from '@mui/icons-material';
import { ezpPalette } from '../../components/EzpLayout';
import api from '../../services/api';

export default function EzpProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('ezp');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceRate, setPriceRate] = useState(1.4);

  useEffect(() => {
    setLoading(true);
    api.get(`/ezp/products/${id}`).then(res => {
      if (res.data.success) setProduct(res.data.product);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: ezpPalette.primary }} />
      </Box>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5">{t('detail.not_found', '商品不存在')}</Typography>
        <Button onClick={() => navigate('/products')} sx={{ mt: 2 }}>{t('detail.back', '返回商品列表')}</Button>
      </Container>
    );
  }

  const costPrice = parseFloat(product.shop_price) || 0;
  const sellingPrice = Math.round(costPrice * priceRate);
  const profit = sellingPrice - costPrice;
  const profitMargin = costPrice > 0 ? Math.round((profit / sellingPrice) * 100) : 0;

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Button startIcon={<BackIcon />} onClick={() => navigate('/products')} sx={{ mb: 3, color: ezpPalette.textMuted }}>
          {t('detail.back', '返回商品列表')}
        </Button>

        <Grid container spacing={4}>
          {/* Product image */}
          <Grid item xs={12} md={5}>
            <Box sx={{
              borderRadius: 3, overflow: 'hidden', border: `1px solid ${ezpPalette.border}`, bgcolor: 'white',
            }}>
              <Box
                component="img"
                src={product.image_url || product.goods_img || '/images/placeholder.webp'}
                alt={product.goods_name}
                sx={{ width: '100%', height: 'auto', maxHeight: 500, objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Grid>

          {/* Product info */}
          <Grid item xs={12} md={7}>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, lineHeight: 1.3 }}>
              {product.goods_name}
            </Typography>

            {product.goods_brief && (
              <Typography variant="body1" sx={{ color: ezpPalette.textMuted, mb: 3, lineHeight: 1.8 }}>
                {product.goods_brief}
              </Typography>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Chip label={`SKU: ${product.goods_sn || product.goods_id}`} variant="outlined" size="small" />
              {product.goods_number > 0 && (
                <Chip label={`${t('detail.stock', '庫存')}: ${product.goods_number}`} color="success" size="small" />
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Profit calculator */}
            <Card elevation={0} sx={{ bgcolor: '#F1F8E9', border: `1px solid #C5E1A5`, borderRadius: 2, p: 3, mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <ProfitIcon sx={{ color: ezpPalette.primary }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: ezpPalette.primary }}>
                  {t('detail.profit_calc', '利潤試算')}
                </Typography>
              </Stack>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: ezpPalette.textMuted }}>{t('detail.cost', '進貨價')}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: ezpPalette.text }}>
                    ${costPrice}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: ezpPalette.textMuted }}>{t('detail.sell_price', '建議售價')}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: ezpPalette.secondary }}>
                    ${sellingPrice}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: ezpPalette.textMuted }}>{t('detail.profit', '每件利潤')}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: ezpPalette.primary }}>
                    ${profit}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: ezpPalette.textMuted }}>{t('detail.margin', '利潤率')}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: ezpPalette.primary }}>
                    {profitMargin}%
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('detail.rate_label', '售價倍率')}: {priceRate.toFixed(1)}x
                </Typography>
                <Slider
                  value={priceRate}
                  onChange={(_, v) => setPriceRate(v)}
                  min={1.1} max={3.0} step={0.1}
                  marks={[
                    { value: 1.4, label: '1.4x' },
                    { value: 2.0, label: '2.0x' },
                    { value: 3.0, label: '3.0x' },
                  ]}
                  sx={{ color: ezpPalette.primary }}
                />
              </Box>
            </Card>

            {/* CTA buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained" size="large" fullWidth startIcon={<CartIcon />}
                href="https://urstore.tw/apply"
                target="_blank"
                sx={{ bgcolor: ezpPalette.secondary, fontWeight: 900, borderRadius: 2, py: 1.5, '&:hover': { bgcolor: '#E65100' } }}
              >
                {t('detail.import_btn', '開店並上架此商品')}
              </Button>
              <Button
                variant="outlined" size="large" fullWidth
                onClick={() => navigate('/products')}
                sx={{ borderColor: ezpPalette.border, color: ezpPalette.text, fontWeight: 700, borderRadius: 2, '&:hover': { borderColor: ezpPalette.primary } }}
              >
                {t('detail.continue', '繼續選品')}
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Product description */}
        {product.goods_desc && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              {t('detail.description', '商品描述')}
            </Typography>
            <Card elevation={0} sx={{ border: `1px solid ${ezpPalette.border}`, borderRadius: 2, p: 3 }}>
              <Box dangerouslySetInnerHTML={{ __html: product.goods_desc }} sx={{ '& img': { maxWidth: '100%' } }} />
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
}
